import React, { useMemo } from 'react';
import { buildSlaKpiReport, measurementCriteria, NA_LABEL } from './dublin-sla-kpi-rules';
import { sanitizeExcelWorkbook } from '_utils/sanitize-spreadsheet-cell';

// DAA's PRM SLA Performance Report: the Aug-26 workbook scoring applied to any
// date range, with the full audit trail visible —
//   Date → Actual → Target → daily Pass/Fail → Period average → Rounded →
//   KPI result → Marks.
// All scoring lives in dublin-sla-kpi-rules.js; this file only renders it.

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function formatDate(dateStr, withWeekday = false) {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dateStr ?? ''));
    if (!m) return String(dateStr ?? '');
    const [, y, mo, d] = m;
    if (!withWeekday) return `${d}/${mo}/${y}`;
    const weekday = WEEKDAYS[new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d))).getUTCDay()];
    return `${weekday} ${d}/${mo}/${y}`;
}

const fmtMarks = (n) => (Number.isInteger(n) ? String(n) : n.toFixed(1));
const fmtPct = (n, dp = 2) => (n == null ? '—' : `${n.toFixed(dp)}%`);

function kpiResultLabel(m) {
    if (m.unavailable) return 'Data unavailable';
    if (m.allNa) return 'Pass (no applicable passengers)';
    return m.met ? 'Achieved' : 'Not achieved';
}

const COLORS = {
    navy: '#1e3a5f',
    blue: '#6b8eb5',
    border: '#e5e7eb',
    text: '#1f2937',
    muted: '#6b7280',
    naText: '#6b7280',
    naBg: '#f9fafb',
    passText: '#166534',
    passBg: '#dcfce7',
    failText: '#b91c1c',
    failBg: '#fee2e2',
    footerBg: '#f3f4f6',
};

const titleBarStyle = {
    backgroundColor: COLORS.navy,
    color: '#fff',
    padding: '14px 18px',
    borderRadius: '8px 8px 0 0',
};

const headerBodyStyle = {
    border: `1px solid ${COLORS.border}`,
    borderTop: 'none',
    borderRadius: '0 0 8px 8px',
    padding: '14px 18px',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '32px',
    alignItems: 'center',
    marginBottom: '16px',
};

const tableStyle = {
    borderCollapse: 'collapse',
    width: '100%',
    fontSize: '12px',
    color: COLORS.text,
};

const thStyle = {
    border: `1px solid ${COLORS.blue}`,
    backgroundColor: COLORS.blue,
    color: '#fff',
    padding: '6px 8px',
    fontWeight: 600,
    textAlign: 'center',
    verticalAlign: 'bottom',
};

const sectionThStyle = {
    ...thStyle,
    backgroundColor: COLORS.navy,
    borderColor: COLORS.navy,
    textAlign: 'left',
    fontSize: '13px',
};

const tdStyle = {
    border: `1px solid ${COLORS.border}`,
    padding: '6px 8px',
    textAlign: 'center',
    verticalAlign: 'middle',
};

const tdLeftStyle = { ...tdStyle, textAlign: 'left' };

const footerTdStyle = { ...tdStyle, backgroundColor: COLORS.footerBg, fontWeight: 600 };
const footerLabelStyle = { ...footerTdStyle, textAlign: 'left', whiteSpace: 'nowrap' };

const STATUS_STYLE = {
    pass: { color: COLORS.passText },
    fail: { color: COLORS.failText, backgroundColor: COLORS.failBg, fontWeight: 700 },
    unavailable: { color: COLORS.muted },
};

const resultStyle = (m) => {
    if (m.unavailable) return { color: COLORS.muted };
    return m.met
        ? { color: COLORS.passText, backgroundColor: COLORS.passBg, fontWeight: 700 }
        : { color: COLORS.failText, backgroundColor: COLORS.failBg, fontWeight: 700 };
};

const buttonStyle = {
    padding: '4px 10px',
    fontSize: '12px',
    cursor: 'pointer',
    backgroundColor: '#dcfce7',
    color: '#166534',
    border: '1px solid #bbf7d0',
    borderRadius: '6px',
};

const noteStyle = { margin: '0 0 6px', fontSize: '12px', color: COLORS.muted };
const warnStyle = { margin: '0 0 6px', fontSize: '12px', color: COLORS.failText };

const SummaryTable = ({ report }) => (
    <div style={{ overflowX: 'auto', marginBottom: '24px' }}>
        <table style={tableStyle}>
            <thead>
                <tr>
                    <th style={{ ...thStyle, textAlign: 'left' }}>KPI</th>
                    <th style={{ ...thStyle, textAlign: 'left' }}>Measurement</th>
                    <th style={thStyle}>Criteria</th>
                    <th style={thStyle}>Target</th>
                    <th style={thStyle}>Max marks</th>
                    <th style={thStyle}>Applicable days</th>
                    <th style={thStyle}>N/A days</th>
                    <th style={thStyle}>Period average</th>
                    <th style={thStyle}>Rounded result</th>
                    <th style={thStyle}>KPI result</th>
                    <th style={thStyle}>Marks achieved</th>
                </tr>
            </thead>
            <tbody>
                {report.sections.map((section) => section.measurements.map((m, i) => (
                    <tr key={`${section.key}-${m.kpiKey}`}>
                        {i === 0 && (
                            <td style={{ ...tdLeftStyle, fontWeight: 700 }} rowSpan={section.measurements.length}>
                                {section.title}
                            </td>
                        )}
                        <td style={tdLeftStyle}>{m.label}</td>
                        <td style={tdStyle}>{measurementCriteria(section, m)}</td>
                        <td style={tdStyle}>{m.target}%</td>
                        <td style={tdStyle}>{fmtMarks(m.maxMarks)}</td>
                        <td style={tdStyle}>{m.applicableDays}</td>
                        <td style={tdStyle}>{m.naDays}</td>
                        <td style={tdStyle}>{fmtPct(m.periodAverage)}</td>
                        <td style={tdStyle}>{m.roundedResult == null ? '—' : `${m.roundedResult}%`}</td>
                        <td style={{ ...tdStyle, ...resultStyle(m) }}>{kpiResultLabel(m)}</td>
                        <td style={{ ...tdStyle, fontWeight: 700 }}>{fmtMarks(m.marks)}</td>
                    </tr>
                )))}
                <tr>
                    <td style={footerLabelStyle} colSpan={4}>Total</td>
                    <td style={footerTdStyle}>{fmtMarks(report.maxMarks)}</td>
                    <td style={footerTdStyle} colSpan={5} />
                    <td style={{ ...footerTdStyle, fontSize: '13px' }}>
                        {fmtMarks(report.marks)} / {fmtMarks(report.maxMarks)}
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
);

// One section: dates down the side, each measurement as an Actual + Status
// column pair, and the period calculation underneath.
const SectionDetail = ({ section, dates }) => {
    const ms = section.measurements;
    const footer = [
        ['Applicable days', (m) => m.applicableDays],
        ['N/A days (treated as pass)', (m) => m.naDays],
        ['Days below target', (m) => m.daysFailed],
        ['Period average', (m) => fmtPct(m.periodAverage)],
        ['Rounded result', (m) => (m.roundedResult == null ? '—' : `${m.roundedResult}%`)],
        ['Target', (m) => `${m.target}%`],
        ['KPI result', (m) => kpiResultLabel(m), resultStyle],
        ['Max marks', (m) => fmtMarks(m.maxMarks)],
        ['Marks achieved', (m) => fmtMarks(m.marks)],
    ];

    return (
        <div style={{ marginBottom: '24px' }}>
            {section.unavailable && (
                <p style={warnStyle}>
                    Data for this KPI failed to load ({section.unavailable}) — it has not been scored. Re-run the report.
                </p>
            )}
            <div style={{ overflowX: 'auto', border: `1px solid ${COLORS.border}`, borderRadius: '8px' }}>
                <table style={{ ...tableStyle, width: 'max-content', minWidth: '100%' }}>
                    <thead>
                        <tr>
                            <th style={sectionThStyle} colSpan={1 + ms.length * 2}>
                                {section.title}
                                <span style={{ float: 'right', fontWeight: 600 }}>
                                    {fmtMarks(section.marks)} / {fmtMarks(section.maxMarks)} marks
                                </span>
                            </th>
                        </tr>
                        <tr>
                            <th style={{ ...thStyle, textAlign: 'left' }} rowSpan={2}>Date</th>
                            {ms.map((m) => (
                                <th key={m.kpiKey} style={{ ...thStyle, minWidth: '190px' }} colSpan={2}>
                                    {m.label}
                                    <div style={{ fontWeight: 400, fontSize: '11px' }}>
                                        {[measurementCriteria(section, m), `Target ${m.target}%`, `Max ${fmtMarks(m.maxMarks)}`].filter(Boolean).join(' · ')}
                                    </div>
                                </th>
                            ))}
                        </tr>
                        <tr>
                            {ms.map((m) => (
                                <React.Fragment key={m.kpiKey}>
                                    <th style={thStyle}>Actual</th>
                                    <th style={thStyle}>Status</th>
                                </React.Fragment>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {dates.map((date, di) => (
                            <tr key={date}>
                                <td style={{ ...tdLeftStyle, fontWeight: 600, whiteSpace: 'nowrap' }}>{formatDate(date, true)}</td>
                                {ms.map((m) => {
                                    const d = m.days[di];
                                    const na = !d.applicable && d.status === 'pass';
                                    return (
                                        <React.Fragment key={m.kpiKey}>
                                            <td style={{ ...tdStyle, ...(na ? { color: COLORS.naText, backgroundColor: COLORS.naBg, fontSize: '11px' } : null) }}>
                                                {d.display}
                                            </td>
                                            <td style={{ ...tdStyle, ...STATUS_STYLE[d.status] }}>{d.statusLabel}</td>
                                        </React.Fragment>
                                    );
                                })}
                            </tr>
                        ))}
                        {footer.map(([label, value, style]) => (
                            <tr key={label}>
                                <td style={footerLabelStyle}>{label}</td>
                                {ms.map((m) => (
                                    <td key={m.kpiKey} style={{ ...footerTdStyle, ...(style ? style(m) : null) }} colSpan={2}>
                                        {value(m)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {ms.some((m) => m.drift) && ms.filter((m) => m.drift).map((m) => (
                <p key={m.kpiKey} style={warnStyle}>
                    {m.label}: scoring mismatch — {m.drift}. Raise with development before issuing this report.
                </p>
            ))}
        </div>
    );
};

async function exportExcel(report, startDate, endDate) {
    const Excel = await import('exceljs/dist/exceljs.min.js');
    const workbook = new Excel.Workbook();
    const border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    const fill = (argb) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb } });
    const style = (cell, { bg, bold, color, align = 'center' } = {}) => {
        cell.border = border;
        cell.alignment = { vertical: 'middle', horizontal: align, wrapText: true };
        if (bg) cell.fill = fill(bg);
        cell.font = { bold: !!bold, color: color ? { argb: color } : undefined };
    };
    const NAVY = 'FF1E3A5F';
    const BLUE = 'FF6B8EB5';
    const WHITE = 'FFFFFFFF';
    const GREY = 'FFF3F4F6';
    const RED_BG = 'FFFEE2E2';
    const RED = 'FFB91C1C';
    const GREEN_BG = 'FFDCFCE7';
    const GREEN = 'FF166534';
    const resultFmt = (m) => (m.unavailable ? {} : m.met ? { bg: GREEN_BG, color: GREEN, bold: true } : { bg: RED_BG, color: RED, bold: true });

    // Summary sheet
    const sum = workbook.addWorksheet('Summary');
    [34, 44, 22, 10, 11, 12, 10, 14, 13, 26, 13].forEach((w, i) => { sum.getColumn(i + 1).width = w; });
    const t = sum.addRow(['PRM SLA PERFORMANCE REPORT']);
    sum.mergeCells(t.number, 1, t.number, 11);
    style(t.getCell(1), { bg: NAVY, color: WHITE, bold: true, align: 'left' });
    sum.addRow([`From: ${formatDate(startDate)}`, `To: ${formatDate(endDate)}`]);
    sum.addRow([`Overall Score: ${fmtMarks(report.marks)} / ${fmtMarks(report.maxMarks)}`]).getCell(1).font = { bold: true, size: 13 };
    sum.addRow([]);
    const head = sum.addRow(['KPI', 'Measurement', 'Criteria', 'Target', 'Max marks', 'Applicable days', 'N/A days', 'Period average', 'Rounded result', 'KPI result', 'Marks achieved']);
    head.eachCell((c) => style(c, { bg: BLUE, color: WHITE, bold: true }));
    report.sections.forEach((section) => section.measurements.forEach((m) => {
        const row = sum.addRow([
            section.title, m.label, measurementCriteria(section, m), `${m.target}%`, m.maxMarks,
            m.applicableDays, m.naDays, m.periodAverage == null ? '—' : Number(m.periodAverage.toFixed(2)),
            m.roundedResult == null ? '—' : `${m.roundedResult}%`, kpiResultLabel(m), m.marks,
        ]);
        row.eachCell({ includeEmpty: true }, (c, col) => style(c, col <= 2 ? { align: 'left' } : col === 10 ? resultFmt(m) : {}));
    }));
    const tot = sum.addRow(['Total', '', '', '', report.maxMarks, '', '', '', '', '', `${fmtMarks(report.marks)} / ${fmtMarks(report.maxMarks)}`]);
    tot.eachCell({ includeEmpty: true }, (c) => style(c, { bg: GREY, bold: true }));

    // Daily detail: one block per section, stacked
    const det = workbook.addWorksheet('Daily Detail');
    det.getColumn(1).width = 26;
    report.sections.forEach((section) => {
        const ms = section.measurements;
        const width = 1 + ms.length * 2;
        for (let c = 2; c <= width; c++) det.getColumn(c).width = Math.max(det.getColumn(c).width || 0, 16);

        const title = det.addRow([`${section.title}  —  ${fmtMarks(section.marks)} / ${fmtMarks(section.maxMarks)} marks`]);
        det.mergeCells(title.number, 1, title.number, width);
        style(title.getCell(1), { bg: NAVY, color: WHITE, bold: true, align: 'left' });

        const h1 = det.addRow(['Date', ...ms.flatMap((m) => [`${m.label} (${[measurementCriteria(section, m), `T${m.target}%`].filter(Boolean).join(', ')})`, ''])]);
        ms.forEach((_, i) => det.mergeCells(h1.number, 2 + i * 2, h1.number, 3 + i * 2));
        h1.eachCell({ includeEmpty: true }, (c) => style(c, { bg: BLUE, color: WHITE, bold: true }));
        h1.height = 36;
        const h2 = det.addRow(['', ...ms.flatMap(() => ['Actual', 'Status'])]);
        h2.eachCell({ includeEmpty: true }, (c) => style(c, { bg: BLUE, color: WHITE, bold: true }));

        report.dates.forEach((date, di) => {
            const row = det.addRow([formatDate(date, true), ...ms.flatMap((m) => [m.days[di].display, m.days[di].statusLabel])]);
            style(row.getCell(1), { bold: true, align: 'left' });
            ms.forEach((m, i) => {
                const d = m.days[di];
                style(row.getCell(2 + i * 2), d.display === NA_LABEL ? { color: 'FF6B7280' } : {});
                style(row.getCell(3 + i * 2), d.status === 'fail' ? { bg: RED_BG, color: RED, bold: true } : d.status === 'pass' ? { color: GREEN } : {});
            });
        });

        const footer = [
            ['Applicable days', (m) => m.applicableDays],
            ['N/A days (treated as pass)', (m) => m.naDays],
            ['Days below target', (m) => m.daysFailed],
            ['Period average', (m) => fmtPct(m.periodAverage)],
            ['Rounded result', (m) => (m.roundedResult == null ? '—' : `${m.roundedResult}%`)],
            ['Target', (m) => `${m.target}%`],
            ['KPI result', (m) => kpiResultLabel(m)],
            ['Max marks', (m) => m.maxMarks],
            ['Marks achieved', (m) => m.marks],
        ];
        footer.forEach(([label, value]) => {
            const row = det.addRow([label, ...ms.flatMap((m) => [value(m), ''])]);
            ms.forEach((_, i) => det.mergeCells(row.number, 2 + i * 2, row.number, 3 + i * 2));
            row.eachCell({ includeEmpty: true }, (c, col) => {
                const m = ms[Math.floor((col - 2) / 2)];
                style(c, label === 'KPI result' && m ? { bg: GREY, ...resultFmt(m) } : { bg: GREY, bold: true, align: col === 1 ? 'left' : 'center' });
            });
        });
        det.addRow([]);
    });

    sanitizeExcelWorkbook(workbook);
    const buf = await workbook.xlsx.writeBuffer();
    const link = document.createElement('a');
    link.href = URL.createObjectURL(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
    link.download = `dublin-prm-sla-kpi-${startDate}_${endDate}.xlsx`;
    link.click();
    URL.revokeObjectURL(link.href);
}

const DublinSlaKpiReportTable = ({ daily, errors, startDate, endDate }) => {
    const report = useMemo(
        () => (startDate && endDate ? buildSlaKpiReport({ daily, errors, startDate, endDate }) : null),
        [daily, errors, startDate, endDate]
    );

    if (!report) {
        return <p style={{ fontSize: '14px', color: COLORS.muted }}>Run the report to see results.</p>;
    }

    return (
        <div style={{ marginBottom: '24px' }}>
            <div style={titleBarStyle}>
                <div style={{ fontSize: '16px', fontWeight: 700, letterSpacing: '0.5px' }}>PRM SLA PERFORMANCE REPORT</div>
            </div>
            <div style={headerBodyStyle}>
                <div>
                    <div style={{ fontSize: '11px', color: COLORS.muted }}>From</div>
                    <div style={{ fontSize: '14px', fontWeight: 600 }}>{formatDate(startDate)}</div>
                </div>
                <div>
                    <div style={{ fontSize: '11px', color: COLORS.muted }}>To</div>
                    <div style={{ fontSize: '14px', fontWeight: 600 }}>{formatDate(endDate)}</div>
                </div>
                <div>
                    <div style={{ fontSize: '11px', color: COLORS.muted }}>Overall Score</div>
                    <div style={{ fontSize: '22px', fontWeight: 700, color: COLORS.navy }}>
                        {fmtMarks(report.marks)} / {fmtMarks(report.maxMarks)}
                    </div>
                </div>
                <div style={{ marginLeft: 'auto' }}>
                    <button type="button" onClick={() => exportExcel(report, startDate, endDate)} style={buttonStyle}>
                        Export Excel
                    </button>
                </div>
            </div>

            <p style={noteStyle}>
                Period result = average of the daily results, each applicable day weighted equally, rounded to the
                nearest whole % (.50 and above rounds up) and compared with the target. Marks are all or nothing per
                KPI. <strong>{NA_LABEL}</strong> is treated as a pass for that day
                {report.naMode === 'as100' ? ' and counts as 100% in the period average' : ' and is not included in the period average'};
                a KPI with no applicable passengers across the whole range receives full marks.
            </p>
            {report.hasUnavailable && (
                <p style={warnStyle}>
                    One or more KPIs could not be loaded and have scored 0 marks. Re-run the report before issuing it.
                </p>
            )}

            <SummaryTable report={report} />

            {report.sections.map((section) => (
                <SectionDetail key={section.key} section={section} dates={report.dates} />
            ))}
        </div>
    );
};

export default DublinSlaKpiReportTable;
